import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  balanceCents: number;
  createdAt: number;
  lastLoginAt: number | null;
}

const TYPE_OPTIONS = [
  { value: 'admin_adjust', label: '后台调整' },
  { value: 'gift', label: '赠送' },
  { value: 'refund', label: '退款' },
];

const fmtYuan = (cents: number) => `¥${((cents ?? 0) / 100).toFixed(2)}`;
const fmtTime = (ts: number | null) =>
  ts ? new Date(ts * 1000).toLocaleString('zh-CN') : '—';

export default function AdminUsers() {
  const [items, setItems] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [type, setType] = useState('admin_adjust');
  const [saving, setSaving] = useState(false);

  // 添加用户
  const [adding, setAdding] = useState(false);
  const [nEmail, setNEmail] = useState('');
  const [nName, setNName] = useState('');
  const [nRole, setNRole] = useState('user');
  const [nPwd, setNPwd] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    const res = await api<{ items: UserRow[]; total: number }>(
      `/admin/users?q=${encodeURIComponent(q)}`
    );
    if (res.ok && res.data) {
      setItems(res.data.items);
      setTotal(res.data.total);
    } else {
      setError(errMsg(res, '加载失败'));
    }
    setLoading(false);
  }

  // 搜索防抖
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function submit() {
    if (!editing) return;
    const yuan = Number(amount);
    if (!Number.isFinite(yuan)) {
      setError('请输入有效的金额');
      return;
    }
    setSaving(true);
    setError('');
    const res = await api(`/admin/users/${editing.id}/balance`, {
      method: 'POST',
      body: JSON.stringify({
        amountCents: Math.round(yuan * 100),
        note: note || undefined,
        type,
      }),
    });
    setSaving(false);
    if (res.ok) {
      setEditing(null);
      setAmount('');
      setNote('');
      load();
    } else {
      setError(errMsg(res, '操作失败'));
    }
  }

  async function addUser() {
    setSaving(true);
    setError('');
    const res = await api('/admin/users', {
      method: 'POST',
      body: JSON.stringify({
        email: nEmail,
        displayName: nName,
        password: nPwd,
        role: nRole,
      }),
    });
    setSaving(false);
    if (res.ok) {
      setAdding(false);
      setNEmail('');
      setNName('');
      setNPwd('');
      setNRole('user');
      load();
    } else {
      setError(errMsg(res, '创建失败'));
    }
  }

  async function setStatus(u: UserRow, status: 'active' | 'banned') {
    const res = await api(`/admin/users/${u.id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
    if (res.ok) load();
    else setError(errMsg(res, '操作失败'));
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索邮箱或昵称…"
          className="h-10 w-72 rounded-xl border border-ink-200 bg-white px-3 text-sm outline-none focus:border-ink-500"
        />
        <span className="text-xs text-ink-500">共 {total} 名用户</span>
        <button
          onClick={() => {
            setAdding(true);
            setError('');
          }}
          className="ml-auto rounded-full bg-ink-900 px-4 py-1.5 text-xs text-ink-50"
        >
          + 添加用户
        </button>
      </div>

      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-ink-500">加载中…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
              <tr>
                <th className="px-4 py-3">用户</th>
                <th className="px-4 py-3">角色</th>
                <th className="px-4 py-3">余额</th>
                <th className="px-4 py-3">注册时间</th>
                <th className="px-4 py-3">最近登录</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-3">
                    <div className="text-ink-900">{u.displayName}</div>
                    <div className="text-xs text-ink-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-600">
                    <div>{u.role === 'admin' ? '管理员' : '普通用户'}</div>
                    {u.status === 'banned' && (
                      <span className="mt-1 inline-block rounded-full bg-red-50 px-2 py-0.5 text-[10px] text-red-600">
                        已禁用
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-candle-600">{fmtYuan(u.balanceCents)}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">{fmtTime(u.createdAt)}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">{fmtTime(u.lastLoginAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setStatus(u, u.status === 'banned' ? 'active' : 'banned')}
                      className={`mr-2 rounded-lg px-3 py-1.5 text-xs ${
                        u.status === 'banned'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-red-50 text-red-600'
                      }`}
                    >
                      {u.status === 'banned' ? '启用' : '禁用'}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(u);
                        setError('');
                      }}
                      className="rounded-lg bg-ink-900 px-3 py-1.5 text-xs text-ink-50 transition hover:bg-ink-800"
                    >
                      添加钱
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-ink-400">
                    没有匹配的用户
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6"
          onClick={() => setEditing(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serifcn text-lg text-ink-900">给用户加余额</h3>
            <p className="mt-1 text-xs text-ink-500">
              {editing.displayName} · {editing.email} · 当前 {fmtYuan(editing.balanceCents)}
            </p>

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">
              金额（元，正数为入账）
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="如 100 表示充值 ¥100"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            />

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">类型</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">备注（选填）</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="如：活动赠送 / 投诉补偿"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            />

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setEditing(null)}
                className="h-11 flex-1 rounded-xl border border-ink-300 text-sm text-ink-700"
              >
                取消
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="h-11 flex-1 rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-50"
              >
                {saving ? '处理中…' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {adding && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6"
          onClick={() => setAdding(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serifcn text-lg text-ink-900">添加用户</h3>

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">邮箱</label>
            <input
              value={nEmail}
              onChange={(e) => setNEmail(e.target.value)}
              placeholder="user@example.com"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            />

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">昵称</label>
            <input
              value={nName}
              onChange={(e) => setNName(e.target.value)}
              placeholder="用户昵称"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            />

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">角色</label>
            <select
              value={nRole}
              onChange={(e) => setNRole(e.target.value)}
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            >
              <option value="user">普通用户</option>
              <option value="admin">管理员</option>
            </select>

            <label className="mb-1.5 mt-4 block text-xs text-ink-600">登录密码（至少 6 位）</label>
            <input
              type="password"
              value={nPwd}
              onChange={(e) => setNPwd(e.target.value)}
              placeholder="初始密码"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500"
            />

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setAdding(false)}
                className="h-11 flex-1 rounded-xl border border-ink-300 text-sm text-ink-700"
              >
                取消
              </button>
              <button
                onClick={addUser}
                disabled={saving}
                className="h-11 flex-1 rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-50"
              >
                {saving ? '处理中…' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
